import type { CSSProperties } from "react";

/**
 * The mobile app's read-only card, value for value: this mirrors `readOnlyStyles` in
 * apps/b2b-mobile/src/features/history/components/readonly/ReadOnlyKit.tsx so a teacher reviewing
 * a play sees what the student saw. Inline rather than Tailwind because the numbers are mobile's
 * own — rounding them to the nearest utility class is what made the two drift apart before.
 *
 * React Native lays a View out as a column flexbox, so anything that carried `gap` there needs
 * `display: flex` here to keep the same spacing.
 */

export const FONT = "'KumbhSans', sans-serif";

/** From packages/ui-mobile/src/theme — the light theme the app ships. */
export const COLORS = {
  white: "#FFFFFF",
  text: "#1A1A1A",
  textSecondary: "#666666",
  textTertiary: "#999999",
  success: "#97D100",
  warning: "#FFAC27",
  error: "#FF6D6D",
} as const;

export const SPACING = { xs: 4, sm: 8, md: 16, lg: 20, xl: 24 } as const;

/**
 * Web only, where plays are stepped through one after another: the canvas replay's ceiling, and
 * the height every card follows so moving between templates never shifts the page. The 88 is the
 * card's own chrome — padding, header row, gap — leaving the replay its full height.
 */
export const REPLAY_HEIGHT = 600;
export const CARD_HEIGHT = REPLAY_HEIGHT + 88;

const column: CSSProperties = { display: "flex", flexDirection: "column" };
const row: CSSProperties = { display: "flex", flexDirection: "row" };

export const rs = {
  card: {
    ...column,
    width: "100%",
    maxWidth: 480,
    height: CARD_HEIGHT,
    backgroundColor: COLORS.white,
    borderRadius: 24,
    padding: SPACING.lg,
    gap: SPACING.md,
    boxShadow: "0 6px 16px rgba(0, 0, 0, 0.15)",
    fontFamily: FONT,
    color: COLORS.text,
  },
  headerRow: {
    ...row,
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: SPACING.sm,
  },
  title: {
    flex: 1,
    fontSize: 18,
    fontWeight: 800,
    color: "#2626A5",
    margin: 0,
  },
  scorePill: {
    borderWidth: 2,
    borderStyle: "solid",
    borderRadius: 999,
    padding: "4px 12px",
    whiteSpace: "nowrap",
  },
  scorePillText: { fontSize: 16, fontWeight: 800 },
  bodyContent: { ...column, flex: 1, minHeight: 0, overflowY: "auto", gap: SPACING.md },

  section: { ...column, gap: SPACING.xs },
  sectionLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: "#8A8A8A",
    letterSpacing: 1,
    textTransform: "uppercase",
    margin: 0,
  },

  answerLine: { ...column, gap: 2, padding: `${SPACING.xs}px 0` },
  answerLabel: { fontSize: 14, fontWeight: 600, color: "#4B4B4B", margin: 0 },
  answerRow: {
    ...row,
    alignItems: "center",
    justifyContent: "space-between",
    gap: SPACING.sm,
  },
  answerText: { flex: 1, fontSize: 15, fontWeight: 700, minWidth: 0 },
  answerScore: { fontSize: 14, fontWeight: 800 },
  correctText: { fontSize: 13, fontWeight: 600, color: "#F4872A", margin: 0 },

  option: {
    ...row,
    alignItems: "center",
    gap: SPACING.sm,
    borderWidth: 2,
    borderStyle: "solid",
    borderColor: "#E5E7EB",
    borderRadius: 16,
    padding: `${SPACING.sm}px ${SPACING.md}px`,
  },
  optionCorrect: { borderColor: "#22c55e", backgroundColor: "#f0fdf4" },
  optionWrong: { borderColor: "#ef4444", backgroundColor: "#fef2f2" },
  optionText: { flex: 1, fontSize: 16, fontWeight: 700, color: "#4B4B4B", minWidth: 0 },
  optionTextCorrect: { color: "#166534" },
  optionTextWrong: { color: "#991b1b" },
  pickedTag: {
    fontSize: 11,
    fontWeight: 700,
    color: "#6B7280",
    letterSpacing: 0.5,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },

  speaker: {
    ...row,
    flexShrink: 0,
    width: 36,
    height: 36,
    borderRadius: 18,
    border: "none",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 0,
  },
  speakerPrompt: { backgroundColor: "#F1F0FB", color: "#2C2B80" },
  speakerStudent: { backgroundColor: "#FFF7DD", color: "#F4872A" },
  speakerDisabled: { opacity: 0.4, cursor: "default" },

  missingMedia: {
    ...row,
    alignItems: "center",
    gap: SPACING.xs,
    backgroundColor: "#F6F6F6",
    borderRadius: 12,
    padding: `${SPACING.sm}px ${SPACING.md}px`,
  },
  missingMediaText: { fontSize: 13, color: "#8A8A8A" },

  promptImage: {
    width: "100%",
    aspectRatio: "1",
    borderRadius: 16,
    backgroundColor: "#F3F4F6",
    objectFit: "cover",
  },
  promptVideo: {
    width: "100%",
    aspectRatio: "16 / 9",
    borderRadius: 16,
    backgroundColor: "#000",
    objectFit: "contain",
  },
  feedbackBox: {
    ...column,
    gap: SPACING.xs,
    backgroundColor: "#F5F3FF",
    borderLeft: "3px solid #7C3AED",
    borderRadius: 12,
    padding: SPACING.md,
  },
  feedbackLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: "#6D28D9",
    letterSpacing: 1,
    textTransform: "uppercase",
    margin: 0,
  },
  feedbackText: { fontSize: 14, lineHeight: "20px", color: "#3B3355", margin: 0 },
  emptyText: {
    fontSize: 14,
    color: "#8A8A8A",
    textAlign: "center",
    padding: `${SPACING.lg}px 0`,
    margin: 0,
  },

  hero: {
    ...column,
    alignItems: "center",
    gap: SPACING.sm,
    backgroundColor: "#FBFAFF",
    borderRadius: 20,
    padding: `${SPACING.lg}px ${SPACING.md}px`,
  },
  heroWord: {
    fontSize: 34,
    fontWeight: 800,
    color: "#1e1b4b",
    textAlign: "center",
    margin: 0,
    overflowWrap: "anywhere",
  },
  heroPhoneme: {
    fontSize: 64,
    fontWeight: 900,
    color: "#1e1b4b",
    lineHeight: "74px",
    overflowWrap: "anywhere",
  },
  heroSubtext: {
    fontSize: 14,
    color: COLORS.textSecondary,
    textAlign: "center",
    margin: 0,
  },
} satisfies Record<string, CSSProperties>;
